import type { PollInsights, Slot, VoteType } from "./types";

export interface Blocker {
  /** Participant id who blocks this slot */
  participantId: string;
  /** Display name */
  name: string;
  /** Vote type — "no" prioritized over "maybe", null = didn't vote */
  vote: VoteType | null;
}

export interface NearMissSlot {
  slotId: string;
  /** yes count for the slot */
  yes: number;
  /** Gap relative to the winner's yes count (winnerYes - thisYes), 1..3 */
  gap: number;
  /** Blockers : people who voted no/maybe/null on this slot AND yes on the
   * winner. Limited to the size of `gap` (the participants who, if flipped,
   * would tie or surpass the winner). Sorted: no > maybe > null, then name. */
  blockers: Blocker[];
}

export interface BlockersInput {
  participantId: string;
  name: string;
  /** vote per slotId */
  votes: Record<string, VoteType>;
}

const VOTE_PRIORITY: Record<string, number> = {
  no: 0,
  maybe: 1,
  // null/undefined goes last (priority 2)
};

function votePriority(v: VoteType | null | undefined): number {
  if (v === "no") return 0;
  if (v === "maybe") return 1;
  return 2;
}

/**
 * Identifie les créneaux "near-miss" : ceux qui ont 1, 2 ou 3 yes de moins
 * que le slot gagnant, et liste les bloqueurs (votants ayant dit oui au
 * gagnant mais non/peut-être/rien à ce créneau).
 *
 * Règles :
 * - gap = winnerYes - slotYes ; on garde gap ∈ [1, 3]
 * - winner exclu de la liste
 * - blockers : ceux qui ont voté yes au gagnant ET (no | maybe | null) ici
 * - tri blockers : "no" > "maybe" > null, puis nom alphabétique
 * - on tronque la liste à `gap` éléments (les "pivots")
 *
 * Returns [] when there's no near-miss → caller falls back to raw mode.
 */
export function computeBlockers(
  slots: Slot[],
  insights: PollInsights,
  participants: BlockersInput[],
): NearMissSlot[] {
  const winnerId = insights.winnerSlotId;
  if (!winnerId) return [];
  const scoreById = new Map(insights.scores.map((s) => [s.slotId, s]));
  const winnerScore = scoreById.get(winnerId);
  if (!winnerScore || winnerScore.yes <= 0) return [];

  // Subset of participants who voted yes on the winner — only they can
  // be blockers (changing their vote elsewhere can shift the outcome).
  const winnerYesParticipants = participants.filter(
    (p) => p.votes[winnerId] === "yes",
  );

  const nearMisses: NearMissSlot[] = [];
  for (const slot of slots) {
    if (slot.id === winnerId) continue;
    const score = scoreById.get(slot.id);
    if (!score) continue;
    const gap = winnerScore.yes - score.yes;
    if (gap < 1 || gap > 3) continue;

    const candidates: Blocker[] = [];
    for (const p of winnerYesParticipants) {
      const v = p.votes[slot.id];
      if (v === "yes") continue; // not a blocker — voted yes here too
      candidates.push({
        participantId: p.participantId,
        name: p.name,
        vote: v ?? null,
      });
    }

    candidates.sort((a, b) => {
      const pa = votePriority(a.vote);
      const pb = votePriority(b.vote);
      if (pa !== pb) return pa - pb;
      return a.name.localeCompare(b.name, "fr");
    });

    nearMisses.push({
      slotId: slot.id,
      yes: score.yes,
      gap,
      // Truncate to gap : the pivotal blockers
      blockers: candidates.slice(0, gap),
    });
  }

  // Sort: smallest gap first (closest to winner)
  nearMisses.sort((a, b) => a.gap - b.gap);
  return nearMisses;
}
